/* Gourmandises & Cie — apparition des sections et nuages (skill `animate`, prototype v3 du 17/09).
   Porte : surfaces vues occasionnellement (accueil, pages éditoriales, fiche produit) ; but :
   prévenir un changement brutal quand un bloc arrive à l'écran, et un peu de délice réservé aux
   entrées (rebond des icônes) et aux Nuages jurassiens. Jamais sur l'en-tête, le panier, les pages
   rayon, les filtres, le checkout ni le héros (qui a sa propre mécanique dans sa section).

   Recettes (attribut data-gcie-recette sur la cible, états finaux par transitions CSS, sans rAF) :
     bloc    glissement 16 px dans le sens de la section + opacité, 350 ms, cascade 80 ms
     titre   groupe de tête : chaque enfant (data-gcie-enfant="ligne") glisse ; surtitre et titre à
             0 ms, première ligne +80 ms, puis +80 ms par ligne (--gcie-d posé ici)
     icones  colonne à icône : l'icône (data-gcie-enfant="icone") arrive avec rebond, titre +140 ms,
             texte +200 ms ; cascade 90 ms entre colonnes
     carte   carte rayon / produit : 24 px, 400 ms, cascade 70 ms
     fondu   média d'un media-with-content : opacité seule, 500 ms
   États data-gcie-reveal : "" en attente, "in" entré, "now" état final sans transition (section
   rechargée dans l'éditeur, ou cible au-dessus du héros). Depuis le 17/09, les cibles visibles au
   chargement JOUENT leur entrée (décision Franck) : plus de "now" pour elles.
   Sens du glissement : data-gcie-sens sur la .shopify-section, lu depuis le bloc maison
   gcie-motion-reglages (<span hidden data-gcie-sens>) ; défaut bas → haut (CSS).
   Nuages : voir la section « NUAGES » plus bas.
   Vanilla, chargé en `defer` par layout/theme.liquid après gcie-header.js. */
(() => {
  'use strict';
  const html = document.documentElement;
  const main = document.getElementById('MainContent');
  if (!main || html.classList.contains('gcie-motion')) return;
  if (!('IntersectionObserver' in window)) return;

  // Portée : gabarits index, page.* et product.* (`{{ template }}` posé par Horizon sur #MainContent).
  const GABARIT = main.dataset.template || '';
  if (!/^(index|page|product)(\.|$)/.test(GABARIT)) return;

  const DESIGN = Boolean(window.Shopify && window.Shopify.designMode);
  const RECETTES = ['bloc', 'titre', 'icones', 'carte', 'fondu'];
  const SEUIL = 0.15;
  const etat = { observes: 0, entres: 0, immediats: 0, recettes: {}, nuages: { sections: 0, nuages: 0 }, sens: {} };
  RECETTES.forEach((r) => { etat.recettes[r] = { observes: 0, entres: 0, immediats: 0 }; });

  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const rootH = e.rootBounds ? e.rootBounds.height : window.innerHeight;
        // Un bloc plus haut que la fenêtre n'atteindrait jamais 15 % : il entre dès son premier pixel.
        if (e.intersectionRatio < SEUIL && e.boundingClientRect.height <= rootH) continue;
        entrer(e.target);
      }
    },
    { threshold: [0, SEUIL], rootMargin: '0px 0px -10% 0px' }
  );

  function entrer(el) {
    io.unobserve(el); // une seule apparition, jamais rejouée au défilement inverse
    el.dataset.gcieReveal = 'in';
    const r = etat.recettes[el.dataset.gcieRecette] || etat.recettes.bloc;
    etat.observes -= 1; etat.entres += 1;
    r.observes -= 1; r.entres += 1;
  }

  // ── Reconnaissance des motifs Horizon ──────────────────────────────────────────────────────
  // Le bloc icône est enveloppé par Horizon (<div class="shopify-block icon-block">) : c'est cette
  // enveloppe, enfant direct du contenu du groupe, qui porte l'entrée.
  const SEL_ICONE = '.icon-block, .icon-block__media, a:has(> .icon-block__media)';
  const SEL_TITRE = '.text-block:is(.h1, .h2, .h3, .h4), .text-block:has(> :is(h1, h2, h3, h4))';
  const contenu = (groupe) => groupe.querySelector(':scope > .group-block-content');
  const enfantsDe = (groupe) => {
    const c = contenu(groupe);
    return c ? Array.from(c.children).filter((el) => !el.matches('script, style, template, .gcie-motion-reglages')) : [];
  };
  const estColonneIcone = (groupe) => enfantsDe(groupe).some((el) => el.matches(SEL_ICONE));
  // Groupe de tête : une colonne de textes dont l'un est un titre, sans icône ni sous-groupe.
  const estGroupeTitre = (groupe) => {
    const c = contenu(groupe);
    if (!c || !c.classList.contains('layout-panel-flex--column')) return false;
    const enfants = enfantsDe(groupe);
    if (!enfants.length || enfants.some((el) => el.matches('.group-block') || el.matches(SEL_ICONE))) return false;
    return enfants.some((el) => el.matches(SEL_TITRE));
  };

  // Pose la recette d'une cible et, pour titre / icones, marque et décale ses enfants.
  function typer(el, recette) {
    el.dataset.gcieRecette = recette;
    if (recette !== 'titre' && recette !== 'icones') return;
    let lignes = 0;
    enfantsDe(el).forEach((enfant) => {
      if (recette === 'icones' && enfant.matches(SEL_ICONE)) {
        enfant.dataset.gcieEnfant = 'icone';
        enfant.style.setProperty('--gcie-d', '0ms');
        return;
      }
      enfant.dataset.gcieEnfant = 'ligne';
      let d;
      if (recette === 'icones') {
        // après l'icône : titre +140 ms, texte +200 ms, puis +60 ms par ligne supplémentaire
        d = lignes === 0 ? 140 : 200 + (lignes - 1) * 60;
        lignes += 1;
      } else {
        // titre : surtitre (préréglage « custom ») et titre restent à 0 ms tant qu'aucune ligne n'a
        // suivi ; première ligne +80 ms, puis +80 ms par ligne
        const tete = enfant.matches(SEL_TITRE) || enfant.matches('.text-block.custom');
        if (tete && lignes === 0) { d = 0; } else { lignes += 1; d = lignes * 80; }
      }
      enfant.style.setProperty('--gcie-d', `${d}ms`);
    });
  }

  // ── Cibles : tableaux [éléments frères, recette] (un tableau = un groupe de décalage) ──────
  function cibles(racine) {
    const groupes = [];
    // media-with-content : le média en fondu, le contenu comme un groupe de tête (glissement).
    racine.querySelectorAll('.media-with-content').forEach((s) => {
      const media = s.querySelector(':scope > .media-block');
      const texte = s.querySelector(':scope > .media-with-content__content');
      if (media) groupes.push([[media], 'fondu']);
      if (texte) groupes.push([[texte], contenu(texte) ? 'titre' : 'bloc']);
    });
    // `section` à groupes (réassurance, terroir, matières…) : les colonnes d'une rangée forment un
    // groupe (icônes si elles en portent) ; un enfant isolé est un groupe de tête ou un bloc.
    racine.querySelectorAll('.section-content-wrapper').forEach((w) => {
      const enfants = Array.from(w.children).filter((el) => !el.matches('script, style, template, .gcie-motion-reglages'));
      if (!enfants.length) return;
      const rangee = (colonnes) => {
        const icones = colonnes.some((c) => c.matches('.group-block') && estColonneIcone(c));
        groupes.push([colonnes, icones ? 'icones' : 'bloc']);
      };
      if (w.classList.contains('layout-panel-flex--row') && enfants.length >= 2) { rangee(enfants); return; }
      enfants.forEach((enfant) => {
        const ligne = enfant.matches('.group-block') ? enfant.querySelector(':scope > .group-block-content.layout-panel-flex--row') : null;
        const colonnes = ligne ? Array.from(ligne.children).filter((el) => el.matches('.group-block')) : [];
        if (colonnes.length >= 2) { rangee(colonnes); return; }
        if (enfant.matches('.group-block') && estColonneIcone(enfant)) { groupes.push([[enfant], 'icones']); return; }
        groupes.push([[enfant], enfant.matches('.group-block') && estGroupeTitre(enfant) ? 'titre' : 'bloc']);
      });
    });
    // En-tête des listes (product-list) : groupe de tête.
    racine.querySelectorAll('.section-resource-list__header > .group-block').forEach((g) => {
      groupes.push([[g], estGroupeTitre(g) ? 'titre' : 'bloc']);
    });
    // collection-list et product-list : les cartes, groupées par liste. L'enfant direct de
    // `.shopify-section` exclut product-recommendations (enveloppé dans <product-recommendations>).
    const listes = new Map();
    racine.querySelectorAll('.shopify-section > .section-resource-list .resource-list__item').forEach((item) => {
      const liste = item.closest('.resource-list') || item.parentElement;
      if (!listes.has(liste)) listes.set(liste, []);
      listes.get(liste).push(item);
    });
    listes.forEach((g) => groupes.push([g, 'carte']));
    return groupes;
  }

  // ── Marquage : mesure d'abord (le transform de l'état masqué déplacerait le rectangle), puis pose.
  //    `immediat` (section rechargée dans l'éditeur) → état final ; sinon tout joue, même ce qui est
  //    déjà visible au chargement — sauf ce qui se trouve entièrement AU-DESSUS du héros. ──────
  function marquer(groupes, immediat) {
    const hero = main.querySelector('.gcie-split-slideshow-section');
    const heroHaut = hero ? hero.getBoundingClientRect().top : -Infinity;
    const aPoser = [];
    groupes.forEach(([g, recette]) => {
      let i = 0;
      g.forEach((el) => {
        if (el.hasAttribute('data-gcie-reveal')) return;
        const r = el.getBoundingClientRect();
        const auDessusDuHero = hero && r.height > 0 && r.bottom <= heroHaut;
        aPoser.push([el, i, recette, immediat || auDessusDuHero]);
        i += 1;
      });
    });
    aPoser.forEach(([el, i, recette, now]) => {
      typer(el, recette);
      el.style.setProperty('--gcie-i', String(Math.min(i, recette === 'carte' ? 5 : 3)));
      const compteur = etat.recettes[recette];
      if (now) { el.dataset.gcieReveal = 'now'; etat.immediats += 1; compteur.immediats += 1; return; }
      el.dataset.gcieReveal = '';
      io.observe(el);
      etat.observes += 1; compteur.observes += 1;
    });
  }

  // ── Réglages par section (bloc maison gcie-motion-reglages) : sens du glissement, nuages ────
  function reglages(racine) {
    const sections = racine.matches('.shopify-section') ? [racine] : Array.from(racine.querySelectorAll('.shopify-section'));
    sections.forEach((section) => {
      const span = section.querySelector('[data-gcie-sens], [data-gcie-nuages]');
      if (!span) return;
      if (span.dataset.gcieSens) {
        section.dataset.gcieSens = span.dataset.gcieSens;
        etat.sens[span.dataset.gcieSens] = (etat.sens[span.dataset.gcieSens] || 0) + 1;
      }
      const nuages = section.querySelector('.media-with-content');
      if (span.dataset.gcieNuages && nuages) nuages.dataset.gcieNuages = span.dataset.gcieNuages;
    });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
     NUAGES — prototype v3 (section 04) : 8 nuages de 64 à 160 px sur deux couches autour de
     l'image des sections media-with-content d'id …__nuages (accueil) et …__recit (fiche signature).
     Positions en % du bloc, écrites pour une image À DROITE ; image à gauche → miroir (right).
     Chaque nuage cumule dérive (translateX), balancement + respiration (translateY + scale) et,
     pour le nuage 3, une traversée linéaire de 60 s. Tout est CSS (gcie-custom.css) : ici, on ne
     fait que poser le balisage. Intensité data-gcie-nuages : aucun · discret · present (défaut).
     ══════════════════════════════════════════════════════════════════════════════════════════ */
  const NUAGES = [
    // [rang, couche, taille px, opacité, dx px, dy px, durée s, phase s, top %, left %, crème ?, visage ?, traverse ?]
    [1, 'arriere', 160, 0.8, 56, 8, 26, 4, 10, 52, false, false, false],
    [4, 'arriere', 80, 0.75, 44, 6, 18, 11, 72, 56, true, false, false],
    [3, 'arriere', 96, 0.7, 0, 8, 24, 20, 36, 0, true, false, true],
    [6, 'arriere', 64, 0.7, 40, 6, 14, 7, 46, 93, false, false, false],
    [2, 'avant', 128, 0.85, 48, 10, 22, 0, 74, 84, true, true, false],
    [5, 'avant', 112, 0.8, 64, 8, 30, 15, 13, 86, false, true, false],
    [7, 'avant', 72, 0.8, 80, 8, 20, 9, 82, 66, false, false, false],
    [8, 'avant', 64, 0.65, 52, 6, 16, 3, 56, 48, true, false, false],
  ];
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const XLINK = 'http://www.w3.org/1999/xlink';
  const svgEl = (nom, attrs) => {
    const el = document.createElementNS(SVG_NS, nom);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    return el;
  };
  const use = (classe, href, y) => {
    const u = svgEl('use', { class: classe, href });
    u.setAttributeNS(XLINK, 'xlink:href', href);
    if (y) u.setAttribute('y', y);
    return u;
  };

  function symboles() {
    if (document.getElementById('gcie-sym-nuage')) return;
    const svg = svgEl('svg', { width: '0', height: '0', 'aria-hidden': 'true', class: 'gcie-symboles' });
    const nuage = svgEl('symbol', { id: 'gcie-sym-nuage', viewBox: '0 0 100 64', overflow: 'visible' });
    nuage.appendChild(svgEl('path', { d: 'M16 50 H84 A10 10 0 0 0 85 30 A14 14 0 0 0 60 20 A17 17 0 0 0 30 24 A12 12 0 0 0 16 50 Z' }));
    const visage = svgEl('symbol', { id: 'gcie-sym-visage', viewBox: '0 0 100 64', overflow: 'visible' });
    visage.appendChild(svgEl('path', { d: 'M36 36 q4 3.5 8 0' }));
    visage.appendChild(svgEl('path', { d: 'M56 36 q4 3.5 8 0' }));
    visage.appendChild(svgEl('path', { d: 'M47 43.5 q3 2.6 6 0' }));
    visage.appendChild(svgEl('circle', { class: 'gcie-nuage__joue', cx: '33', cy: '42', r: '2.6' }));
    visage.appendChild(svgEl('circle', { class: 'gcie-nuage__joue', cx: '67', cy: '42', r: '2.6' }));
    svg.appendChild(nuage);
    svg.appendChild(visage);
    main.prepend(svg);
  }

  function nuage([rang, , taille, op, dx, dy, dur, phase, top, left, creme, visage, traverse], miroir) {
    const div = document.createElement('div');
    div.className = `gcie-nuage${creme ? ' gcie-nuage--creme' : ''}${traverse ? ' gcie-nuage--traverse' : ''}`;
    div.dataset.rang = String(rang);
    div.style.cssText = `--taille:${taille}px;--op:${op};--dx:${dx}px;--dy:${dy}px;--dur:${dur}s;--phase:${phase}s;top:${top}%;`;
    if (!traverse) div.style.setProperty(miroir ? 'right' : 'left', `${left}%`);
    const svg = svgEl('svg', { class: 'gcie-nuage__corps', viewBox: '0 0 100 64', 'aria-hidden': 'true', focusable: 'false' });
    svg.appendChild(use('gcie-nuage__ombre', '#gcie-sym-nuage', '5'));
    svg.appendChild(use('gcie-nuage__fond', '#gcie-sym-nuage'));
    if (visage) svg.appendChild(use('gcie-nuage__visage', '#gcie-sym-visage'));
    div.appendChild(svg);
    return div;
  }

  function nuages(racine) {
    const blocs = racine.querySelectorAll(
      '.shopify-section[id$="__nuages"] .media-with-content, .shopify-section[id$="__recit"] .media-with-content'
    );
    blocs.forEach((bloc) => {
      if (bloc.classList.contains('gcie-nuages')) return;
      symboles();
      const miroir = !bloc.classList.contains('media-with-content--media-right'); // image à gauche → nuages à droite… en miroir
      bloc.classList.add('gcie-nuages');
      if (!bloc.dataset.gcieNuages) bloc.dataset.gcieNuages = 'present';
      ['arriere', 'avant'].forEach((couche) => {
        const ciel = document.createElement('div');
        ciel.className = `gcie-ciel gcie-ciel--${couche}`;
        ciel.setAttribute('aria-hidden', 'true');
        NUAGES.filter((n) => n[1] === couche).forEach((n) => ciel.appendChild(nuage(n, miroir)));
        bloc.appendChild(ciel);
      });
      etat.nuages.sections += 1;
      etat.nuages.nuages += NUAGES.length;
    });
  }

  // ── Départ ──────────────────────────────────────────────────────────────────────────────────
  html.classList.add('gcie-motion');
  reglages(main);
  nuages(main);
  marquer(cibles(main), false);

  // ── Filet ───────────────────────────────────────────────────────────────────────────────────
  // Si l'observateur ne livre rien (audit du 19/09 : onglet d'arrière-plan, extension, moteur
  // exotique), un contenu déjà dans la fenêtre resterait invisible. À chaque arrêt de défilement
  // et 1,5 s après l'arrivée, tout bloc en attente dont le haut est dans la fenêtre entre.
  let filetId = 0;
  function filet() {
    const h = window.innerHeight;
    main.querySelectorAll('[data-gcie-reveal=""]').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < h * 0.9 && r.bottom > 0) entrer(el);
    });
  }
  function filetDiffere(ms) { clearTimeout(filetId); filetId = setTimeout(filet, ms); }
  filetDiffere(1500);
  document.addEventListener('scroll', () => filetDiffere(400), { passive: true, capture: true });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) filetDiffere(600); });

  // Éditeur de thème : une section rechargée réapparaît telle quelle (état final immédiat),
  // avec ses réglages et ses nuages.
  if (DESIGN) {
    document.addEventListener('shopify:section:load', (e) => {
      if (!(e.target instanceof Element) || !main.contains(e.target)) return;
      reglages(e.target);
      nuages(e.target);
      marquer(cibles(e.target), true);
    });
  }

  // Pour la vérification (fenêtre masquée, rAF suspendu : les états finaux ne dépendent que des transitions CSS).
  window.gcieMotionEtat = () => ({
    gabarit: GABARIT,
    observes: etat.observes,
    entres: etat.entres,
    immediats: etat.immediats,
    total: etat.observes + etat.entres + etat.immediats,
    recettes: JSON.parse(JSON.stringify(etat.recettes)),
    nuages: { ...etat.nuages, intensites: Array.from(main.querySelectorAll('.gcie-nuages')).map((b) => b.dataset.gcieNuages) },
    sens: { ...etat.sens },
    reduit: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  });
})();
